import React, { useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  ContentCopy as CopyIcon,
  Refresh as RegenerateIcon,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { ClientConfig } from '../../types';
import { PrimaryButton } from '../common/PrimaryButton';
import { SecondaryButton } from '../common/SecondaryButton';

interface ApiKeySettingsProps {
  config: ClientConfig;
  onConfigUpdated: () => void;
}

export const ApiKeySettings: React.FC<ApiKeySettingsProps> = ({ config, onConfigUpdated }) => {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newKeyInfo, setNewKeyInfo] = useState<{ newApiKey: string; oldKeyExpiresAt: string } | null>(null);

  const displayKey = showKey ? config.apiKey : config.apiKey.replace(/./g, '\u2022').slice(0, 20) + '...';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(config.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenerate = async () => {
    setConfirmOpen(false);
    setRegenerating(true);
    setError(null);

    try {
      const result = await api.regenerateApiKey();
      setNewKeyInfo(result);
      onConfigUpdated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to regenerate API key');
    } finally {
      setRegenerating(false);
    }
  };

  const apiUrl = 'https://api.logopulse.io';

  const sdkSnippet = `import { createLogoPulse } from '@logopulse/sdk';

const logoPulse = createLogoPulse({
  apiKey: '${newKeyInfo?.newApiKey || config.apiKey}',
  apiUrl: '${apiUrl}',
  orgId: '${config.orgId}'
});

// Track events
await logoPulse.track('batch.created', accountId, {
  batchId: 'batch-123'
});`;

  // Check if there's a grace period active
  const hasGracePeriod = config.previousApiKeyExpiresAt && new Date(config.previousApiKeyExpiresAt) > new Date();
  const graceTimeLeft = hasGracePeriod
    ? Math.max(0, Math.ceil((new Date(config.previousApiKeyExpiresAt!).getTime() - Date.now()) / (1000 * 60 * 60)))
    : 0;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {newKeyInfo && (
        <Alert severity="success" sx={{ mb: 2 }}>
          API key regenerated successfully! Your previous key will continue working for 24 hours (until{' '}
          {new Date(newKeyInfo.oldKeyExpiresAt).toLocaleString()}).
        </Alert>
      )}

      {hasGracePeriod && !newKeyInfo && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Previous API key is still valid for {graceTimeLeft} more hour{graceTimeLeft !== 1 ? 's' : ''}.
        </Alert>
      )}

      {/* API Key Display */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          API Key
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#f5f5f5',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {displayKey}
          </Box>
          <Tooltip title={showKey ? 'Hide' : 'Show'}>
            <IconButton size="small" onClick={() => setShowKey(!showKey)}>
              {showKey ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title={copied ? 'Copied!' : 'Copy'}>
            <IconButton size="small" onClick={handleCopy}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box mt={2}>
          <SecondaryButton
            startIcon={<RegenerateIcon />}
            onClick={() => setConfirmOpen(true)}
            disabled={regenerating}
            size="small"
          >
            {regenerating ? 'Regenerating...' : 'Regenerate Key'}
          </SecondaryButton>
        </Box>
      </Paper>

      {/* SDK Code Snippet */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          SDK Integration
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Install the SDK and initialize it with your API key:
        </Typography>

        <Box
          sx={{
            bgcolor: '#1B0826',
            color: '#DCF995',
            borderRadius: 1,
            p: 2,
            fontFamily: 'monospace',
            fontSize: '0.8rem',
            overflow: 'auto',
            whiteSpace: 'pre',
            lineHeight: 1.6,
          }}
        >
          {sdkSnippet}
        </Box>

        <Typography variant="body2" color="text.secondary" mt={2}>
          Install: <code>npm install @logopulse/sdk</code>
        </Typography>
      </Paper>

      {/* Regeneration Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Regenerate API Key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate a new API key. Your current key will continue working for 24 hours
            to give you time to update your integration.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <PrimaryButton onClick={handleRegenerate}>
            Regenerate
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
